import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Visibility,
  Security,
  Warning,
  CheckCircle,
  Error,
  Timeline,
  TrendingUp,
  CalendarToday,
  Speed
} from '@mui/icons-material';
import Footer from '../components/Footer';
import logo from '../assets/logo0.png';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

const ScanHistory = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedScan, setSelectedScan] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('scans')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setScans(data || []);
    } catch (err) {
      console.error('Error fetching scans:', err);
      setError('Failed to load scan history');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Clean': 'success',
      'Threats Detected': 'error',
      'Warning': 'warning'
    };
    return colors[status] || 'default';
  };

  const getThreatLevelColor = (level) => {
    const colors = {
      'Low': 'success',
      'Medium': 'warning',
      'High': 'error'
    };
    return colors[level] || 'default';
  };

  const handleViewDetails = (scan) => {
    setSelectedScan(scan);
    setOpenDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)'
      }}>
        <CircularProgress sx={{ color: '#FFE302' }} size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)'
    }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(90deg, #000000 0%, #1a1a1a 100%)',
        p: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src={logo} 
              alt="Logo" 
              style={{ 
                  height: 40,
                filter: 'brightness(0) invert(1)',
                cursor: 'pointer'
              }} 
              onClick={() => window.location.href = '/dashboard'}
            />
              <Typography 
                variant="h5" 
                sx={{ 
                  ml: 2, 
                  color: '#FFE302', 
                  fontWeight: 700,
                  textShadow: '0px 2px 4px rgba(0,0,0,0.3)'
                }}
              >
              Scan History
            </Typography>
            </Box>
            <Button 
              variant="contained" 
              startIcon={<Security />}
              sx={{ 
                bgcolor: '#FFE302', 
                color: '#000',
                '&:hover': {
                  bgcolor: '#FFD700',
                }
              }}
              onClick={() => window.location.href = '/diagnostics'}
            >
              New Scan
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Analytics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
              color: 'white',
              boxShadow: '0 8px 16px rgba(33, 147, 176, 0.3)',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}>
              <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ 
                  position: 'absolute', 
                  top: -20, 
                  right: -20, 
                  opacity: 0.2,
                  transform: 'rotate(15deg)'
                }}>
                  <TrendingUp sx={{ fontSize: 100 }} />
                </Box>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                  Total Scans
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {scans.length}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Timeline sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Lifetime activity
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)',
              color: 'white',
              boxShadow: '0 8px 16px rgba(255, 65, 108, 0.3)',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}>
              <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ 
                  position: 'absolute', 
                  top: -20, 
                  right: -20, 
                  opacity: 0.2,
                  transform: 'rotate(15deg)'
                }}>
                  <Warning sx={{ fontSize: 100 }} />
                </Box>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                  Threats Detected
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {scans.filter(scan => scan.status === 'Threats Detected').length}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Error sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Requires attention
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              boxShadow: '0 8px 16px rgba(17, 153, 142, 0.3)',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}>
              <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ 
                  position: 'absolute', 
                  top: -20, 
                  right: -20, 
                  opacity: 0.2,
                  transform: 'rotate(15deg)'
                }}>
                  <CheckCircle sx={{ fontSize: 100 }} />
                </Box>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                  Clean Scans
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {scans.filter(scan => scan.status === 'Clean').length}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Security sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    All secure
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4e54c8 0%, #8f94fb 100%)',
              color: 'white',
              boxShadow: '0 8px 16px rgba(78, 84, 200, 0.3)',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}>
              <CardContent sx={{ position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ 
                  position: 'absolute', 
                  top: -20, 
                  right: -20, 
                  opacity: 0.2,
                  transform: 'rotate(15deg)'
                }}>
                  <Speed sx={{ fontSize: 100 }} />
                </Box>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                  Average Duration
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {scans.length ? Math.round(scans.reduce((total, scan) => total + scan.duration, 0) / scans.length) : 0}s
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CalendarToday sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Processing time
                </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          background: 'linear-gradient(to right, #ffffff, #f8f9fa)'
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search scans..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  sx: { 
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.1),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.2),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FFE302',
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                variant="outlined"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                InputProps={{
                  startAdornment: <FilterList sx={{ mr: 1, color: 'text.secondary' }} />,
                  sx: { 
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.1),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha('#000', 0.2),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#FFE302',
                    }
                  }
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="clean">Clean</MenuItem>
                <MenuItem value="threats">Threats Detected</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                startIcon={<Download />}
                fullWidth
                sx={{ 
                  height: '100%',
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #000000 0%, #1a1a1a 100%)',
                  color: '#FFE302',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1a1a1a 0%, #333333 100%)',
                  }
                }}
              >
                Export History
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Scan History Table */}
        <TableContainer component={Paper} sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                background: 'linear-gradient(90deg, #000000 0%, #1a1a1a 100%)'
              }}>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Scan ID</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>URL</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Date & Time</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Threat Level</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Findings</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ color: '#FFE302', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scans
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((scan, index) => (
                <TableRow 
                  key={scan.id}
                  sx={{ 
                    bgcolor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    transition: 'background-color 0.3s',
                    '&:hover': {
                      bgcolor: alpha('#FFE302', 0.05),
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>#{scan.id}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {scan.url}
                  </TableCell>
                  <TableCell>{new Date(scan.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={scan.status}
                      color={getStatusColor(scan.status)}
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={scan.threatLevel}
                      color={getThreatLevelColor(scan.threatLevel)}
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {scan.results || 'No results available'}
                  </TableCell>
                  <TableCell>{scan.type}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Speed sx={{ fontSize: 16, mr: 0.5, color: '#4e54c8' }} />
                      {scan.duration}s
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => handleViewDetails(scan)}
                        sx={{ 
                          color: '#000',
                          bgcolor: alpha('#FFE302', 0.2),
                          '&:hover': {
                            bgcolor: alpha('#FFE302', 0.4),
                          }
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {scans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      No scan history available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={scans.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              bgcolor: '#f8f9fa',
              borderTop: '1px solid rgba(0,0,0,0.05)',
              '& .MuiTablePagination-selectIcon': {
                color: '#000'
              }
            }}
          />
        </TableContainer>
      </Container>

      {/* Scan Details Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }
        }}
      >
        {selectedScan && (
          <>
            <DialogTitle sx={{ 
              background: 'linear-gradient(90deg, #000000 0%, #1a1a1a 100%)',
              color: '#FFE302',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Security /> Scan Details #{selectedScan.id}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper sx={{ 
                    p: 2, 
                    bgcolor: alpha('#FFE302', 0.05),
                    border: '1px solid',
                    borderColor: alpha('#FFE302', 0.2),
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>URL</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedScan.url}</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Scan Date</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarToday sx={{ mr: 1, color: '#4e54c8' }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {new Date(selectedScan.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Duration</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Speed sx={{ mr: 1, color: '#4e54c8' }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedScan.duration} seconds
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Status</Typography>
                  <Chip 
                    label={selectedScan.status}
                    color={getStatusColor(selectedScan.status)}
                      sx={{ 
                        fontWeight: 600,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                  />
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Threat Level</Typography>
                  <Chip 
                    label={selectedScan.threatLevel}
                    color={getThreatLevelColor(selectedScan.threatLevel)}
                      sx={{ 
                        fontWeight: 600,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper sx={{ 
                    p: 2, 
                    borderRadius: 1,
                    bgcolor: selectedScan.status === 'Threats Detected' ? alpha('#f44336', 0.05) : 
                            selectedScan.status === 'Warning' ? alpha('#ff9800', 0.05) : 
                            alpha('#4caf50', 0.05),
                    border: '1px solid',
                    borderColor: selectedScan.status === 'Threats Detected' ? alpha('#f44336', 0.2) : 
                                selectedScan.status === 'Warning' ? alpha('#ff9800', 0.2) : 
                                alpha('#4caf50', 0.2),
                  }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Findings</Typography>
                    <Typography variant="body1">
                      {selectedScan.results || 'No detailed findings available for this scan.'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Button 
                onClick={() => setOpenDialog(false)}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha('#000', 0.05),
                  }
                }}
              >
                Close
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Download />}
                sx={{ 
                  background: 'linear-gradient(90deg, #000000 0%, #1a1a1a 100%)',
                  color: '#FFE302',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1a1a1a 0%, #333333 100%)',
                  }
                }}
              >
                Download Report
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Footer />
    </Box>
  );
};

export default ScanHistory; 